import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
} from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { TransformControls } from 'three/addons/controls/TransformControls.js';
import { makeEntity, makeOpening } from '../model/document';
import type { ModelDocument, ModelEntity, SavedScene, ToolId, Vec3Tuple } from '../model/types';
import { createId } from '../model/id';
import { inferAxis, snapGroundPoint } from '../engine/snap';
import { createDocumentRoot, disposeObject } from '../engine/entityObjects';
import { exportDocument, type ExportFormat } from '../engine/exporters';
import { downloadBlob } from '../utils/fileIO';
import { formatLength } from '../utils/units';
import { useStudioStore } from '../state/useStudioStore';

export type StandardView = 'iso' | 'top' | 'front' | 'back' | 'left' | 'right';

export interface ViewportHandle {
  fitView: (selectionOnly?: boolean) => void;
  setStandardView: (view: StandardView) => void;
  toggleProjection: () => void;
  getProjection: () => 'perspective' | 'orthographic';
  getSceneSnapshot: (name: string) => Omit<SavedScene, 'id'>;
  restoreScene: (scene: SavedScene) => void;
  exportModel: (format: ExportFormat) => Promise<void>;
  capturePng: () => Promise<void>;
}

type NavigableCamera = THREE.PerspectiveCamera | THREE.OrthographicCamera;

interface DrawState {
  firstPoint: THREE.Vector3 | null;
  measurePoint: THREE.Vector3 | null;
  preview: THREE.Object3D | null;
  push: null | {
    entityId: string;
    startClientY: number;
    startHeight: number;
    baseY: number;
    before: ModelDocument;
  };
  transformBefore: ModelDocument | null;
}

const UP = new THREE.Vector3(0, 1, 0);
const DRAW_TOOLS = new Set<ToolId>(['wall', 'room', 'slab', 'box', 'line']);
const TRANSFORM_TOOLS = new Set<ToolId>(['move', 'rotate', 'scale']);

function asTuple(vector: THREE.Vector3): Vec3Tuple {
  return [vector.x, vector.y, vector.z];
}

function setEntityTag(object: THREE.Object3D, id: string): void {
  object.userData.entityId = id;
  object.traverse((child) => { child.userData.entityId = id; });
}

function makePreviewBox(
  width: number,
  height: number,
  depth: number,
  position: THREE.Vector3,
  rotationY = 0,
): THREE.Mesh {
  const mesh = new THREE.Mesh(
    new THREE.BoxGeometry(Math.max(0.001, width), Math.max(0.001, height), Math.max(0.001, depth)),
    new THREE.MeshBasicMaterial({ color: '#35d889', transparent: true, opacity: 0.28, depthWrite: false }),
  );
  mesh.position.copy(position);
  mesh.rotation.y = rotationY;
  mesh.renderOrder = 8;
  return mesh;
}

function disposePreview(object: THREE.Object3D | null): void {
  if (!object) return;
  disposeObject(object);
  object.removeFromParent();
}

function wallFromPoints(
  start: THREE.Vector3,
  end: THREE.Vector3,
  document: ModelDocument,
  name = 'Wall',
): ModelEntity | null {
  const delta = end.clone().sub(start);
  const length = Math.hypot(delta.x, delta.z);
  if (length < 0.05) return null;
  const height = document.settings.wallHeight;
  return makeEntity(
    'wall',
    name,
    [(start.x + end.x) / 2, height / 2, (start.z + end.z) / 2],
    { width: length, height, depth: document.settings.wallThickness },
    {
      rotation: [0, -Math.atan2(delta.z, delta.x), 0],
      color: '#dce2df',
      openings: [],
      metadata: { assembly: 'Generic wall', source: 'ConstructCanvas wall tool' },
    },
  );
}

export const Viewport = forwardRef<ViewportHandle>(function Viewport(_, ref) {
  const mountRef = useRef<HTMLDivElement | null>(null);
  const sceneRef = useRef<THREE.Scene | null>(null);
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const perspectiveRef = useRef<THREE.PerspectiveCamera | null>(null);
  const orthographicRef = useRef<THREE.OrthographicCamera | null>(null);
  const cameraRef = useRef<NavigableCamera | null>(null);
  const controlsRef = useRef<OrbitControls | null>(null);
  const transformRef = useRef<TransformControls | null>(null);
  const modelRootRef = useRef<THREE.Group | null>(null);
  const overlayRootRef = useRef<THREE.Group | null>(null);
  const selectionRootRef = useRef<THREE.Group | null>(null);
  const objectMapRef = useRef(new Map<string, THREE.Object3D>());
  const gridRef = useRef<THREE.GridHelper | null>(null);
  const axesRef = useRef<THREE.AxesHelper | null>(null);
  const cursorMarkerRef = useRef<THREE.Mesh | null>(null);
  const raycasterRef = useRef(new THREE.Raycaster());
  const pointerRef = useRef(new THREE.Vector2());
  const projectionRef = useRef<'perspective' | 'orthographic'>('perspective');
  const interactionRef = useRef<DrawState>({
    firstPoint: null,
    measurePoint: null,
    preview: null,
    push: null,
    transformBefore: null,
  });

  const document = useStudioStore((state) => state.document);
  const selectedIds = useStudioStore((state) => state.selectedIds);
  const hoveredId = useStudioStore((state) => state.hoveredId);
  const activeTool = useStudioStore((state) => state.activeTool);

  function configureOrbit(camera: NavigableCamera, element: HTMLElement): OrbitControls {
    const controls = new OrbitControls(camera, element);
    controls.enableDamping = true;
    controls.dampingFactor = 0.08;
    controls.screenSpacePanning = true;
    controls.minDistance = 0.15;
    controls.maxDistance = 2000;
    controls.mouseButtons.LEFT = null as unknown as THREE.MOUSE;
    controls.mouseButtons.MIDDLE = THREE.MOUSE.ROTATE;
    controls.mouseButtons.RIGHT = THREE.MOUSE.PAN;
    controls.touches.ONE = THREE.TOUCH.ROTATE;
    controls.touches.TWO = THREE.TOUCH.DOLLY_PAN;
    return controls;
  }

  function currentCamera(): NavigableCamera {
    const camera = cameraRef.current;
    if (!camera) throw new Error('Viewport camera is not ready.');
    return camera;
  }

  function currentControls(): OrbitControls {
    const controls = controlsRef.current;
    if (!controls) throw new Error('Viewport controls are not ready.');
    return controls;
  }

  function updateCameraProjection(width: number, height: number): void {
    const aspect = Math.max(0.1, width / Math.max(1, height));
    const perspective = perspectiveRef.current;
    const orthographic = orthographicRef.current;
    if (perspective) {
      perspective.aspect = aspect;
      perspective.updateProjectionMatrix();
    }
    if (orthographic) {
      const span = 14;
      orthographic.left = -span * aspect / 2;
      orthographic.right = span * aspect / 2;
      orthographic.top = span / 2;
      orthographic.bottom = -span / 2;
      orthographic.updateProjectionMatrix();
    }
  }

  function switchProjection(target: 'perspective' | 'orthographic'): void {
    if (!mountRef.current || !cameraRef.current || !controlsRef.current || projectionRef.current === target) return;
    const oldCamera = currentCamera();
    const position = oldCamera.position.clone();
    const quaternion = oldCamera.quaternion.clone();
    const targetPoint = currentControls().target.clone();
    const camera = target === 'perspective' ? perspectiveRef.current : orthographicRef.current;
    if (!camera) return;

    camera.position.copy(position);
    camera.quaternion.copy(quaternion);
    camera.updateProjectionMatrix();
    controlsRef.current?.dispose();
    controlsRef.current = configureOrbit(camera, mountRef.current.querySelector('canvas') as HTMLCanvasElement);
    controlsRef.current.target.copy(targetPoint);
    controlsRef.current.update();
    cameraRef.current = camera;
    projectionRef.current = target;

    const transform = transformRef.current;
    if (transform) (transform as unknown as { camera: NavigableCamera }).camera = camera;
    syncTransformControl();
  }

  function modelBounds(selectionOnly = false): THREE.Box3 {
    const box = new THREE.Box3();
    const ids = useStudioStore.getState().selectedIds;
    if (selectionOnly && ids.length > 0) {
      ids.forEach((id) => {
        const object = objectMapRef.current.get(id);
        if (object) box.expandByObject(object);
      });
    } else if (modelRootRef.current) {
      box.setFromObject(modelRootRef.current);
    }
    if (box.isEmpty()) box.setFromCenterAndSize(new THREE.Vector3(), new THREE.Vector3(8, 4, 8));
    return box;
  }

  function frameBounds(box: THREE.Box3, direction = new THREE.Vector3(1, 0.75, 1)): void {
    // Header actions remain usable if WebGL initialization failed or is not ready.
    if (!cameraRef.current || !controlsRef.current) return;
    const camera = currentCamera();
    const controls = currentControls();
    const centre = box.getCenter(new THREE.Vector3());
    const size = box.getSize(new THREE.Vector3());
    const radius = Math.max(1, size.length() / 2);
    const distance = camera instanceof THREE.PerspectiveCamera
      ? radius / Math.sin(THREE.MathUtils.degToRad(camera.fov / 2)) * 1.15
      : radius * 2.2;
    camera.position.copy(centre).add(direction.normalize().multiplyScalar(distance));
    camera.up.set(0, 1, 0);
    camera.lookAt(centre);
    if (camera instanceof THREE.OrthographicCamera) {
      camera.zoom = Math.max(0.1, 7 / radius);
      camera.updateProjectionMatrix();
    }
    controls.target.copy(centre);
    controls.update();
  }

  function syncTransformControl(): void {
    const transform = transformRef.current;
    if (!transform) return;
    const state = useStudioStore.getState();
    const id = state.selectedIds[0];
    const entity = state.document.entities.find((candidate) => candidate.id === id);
    const object = id ? objectMapRef.current.get(id) : undefined;

    if (!TRANSFORM_TOOLS.has(state.activeTool) || !object || entity?.locked) {
      transform.detach();
      transform.getHelper().visible = false;
      return;
    }

    const mode: 'translate' | 'rotate' | 'scale' = state.activeTool === 'move' ? 'translate' : state.activeTool === 'rotate' ? 'rotate' : 'scale';
    transform.setMode(mode);
    transform.setSpace(state.activeTool === 'move' ? 'world' : 'local');
    transform.setTranslationSnap(state.document.settings.snapEnabled ? state.document.settings.gridSize : null);
    transform.setRotationSnap(state.document.settings.snapEnabled ? THREE.MathUtils.degToRad(5) : null);
    transform.setScaleSnap(state.document.settings.snapEnabled ? 0.05 : null);
    transform.attach(object);
    transform.getHelper().visible = true;
  }

  function updateSelectionHelpers(): void {
    const root = selectionRootRef.current;
    if (!root) return;
    root.children.forEach((child) => disposeObject(child));
    root.clear();

    const state = useStudioStore.getState();
    for (const id of state.selectedIds) {
      const object = objectMapRef.current.get(id);
      if (!object) continue;
      const helper = new THREE.BoxHelper(object, '#19b86b');
      helper.userData.selectionHelper = true;
      root.add(helper);
    }
    if (state.hoveredId && !state.selectedIds.includes(state.hoveredId)) {
      const object = objectMapRef.current.get(state.hoveredId);
      if (object) {
        const helper = new THREE.BoxHelper(object, '#ffffff');
        const material = helper.material as THREE.LineBasicMaterial;
        material.transparent = true;
        material.opacity = 0.72;
        helper.userData.hoverHelper = true;
        root.add(helper);
      }
    }
  }

  function eventPointer(event: PointerEvent): THREE.Vector2 {
    const renderer = rendererRef.current;
    if (!renderer) return pointerRef.current;
    const rect = renderer.domElement.getBoundingClientRect();
    pointerRef.current.set(
      ((event.clientX - rect.left) / rect.width) * 2 - 1,
      -((event.clientY - rect.top) / rect.height) * 2 + 1,
    );
    return pointerRef.current;
  }

  function rayFromEvent(event: PointerEvent): THREE.Raycaster {
    const raycaster = raycasterRef.current;
    raycaster.setFromCamera(eventPointer(event), currentCamera());
    raycaster.params.Line = { threshold: 0.08 };
    return raycaster;
  }

  function hitEntity(event: PointerEvent): { id: string; point: THREE.Vector3; object: THREE.Object3D } | null {
    const root = modelRootRef.current;
    if (!root) return null;
    const hits = rayFromEvent(event).intersectObjects(root.children, true);
    for (const hit of hits) {
      const id = hit.object.userData.entityId as string | undefined;
      if (id) return { id, point: hit.point.clone(), object: hit.object };
    }
    return null;
  }

  function groundPoint(event: PointerEvent): THREE.Vector3 | null {
    const ray = rayFromEvent(event).ray;
    const point = ray.intersectPlane(new THREE.Plane(UP, 0), new THREE.Vector3());
    if (!point) return null;
    return snapGroundPoint(point, useStudioStore.getState().document);
  }

  function setPreview(object: THREE.Object3D | null): void {
    const state = interactionRef.current;
    disposePreview(state.preview);
    state.preview = object;
    if (object && overlayRootRef.current) overlayRootRef.current.add(object);
  }

  function previewLine(start: THREE.Vector3, end: THREE.Vector3, color = '#35d889'): THREE.Line {
    const geometry = new THREE.BufferGeometry().setFromPoints([start, end]);
    const material = new THREE.LineBasicMaterial({ color, transparent: true, opacity: 0.9 });
    const line = new THREE.Line(geometry, material);
    line.position.y += 0.012;
    line.renderOrder = 9;
    return line;
  }

  function updateDrawPreview(tool: ToolId, start: THREE.Vector3, rawEnd: THREE.Vector3): void {
    const state = useStudioStore.getState();
    const end = inferAxis(start, rawEnd.clone());
    const delta = end.clone().sub(start);
    const width = Math.abs(delta.x);
    const depth = Math.abs(delta.z);
    const centre = new THREE.Vector3((start.x + end.x) / 2, 0, (start.z + end.z) / 2);

    if (tool === 'wall') {
      const length = Math.hypot(delta.x, delta.z);
      centre.y = state.document.settings.wallHeight / 2;
      setPreview(makePreviewBox(
        length,
        state.document.settings.wallHeight,
        state.document.settings.wallThickness,
        centre,
        -Math.atan2(delta.z, delta.x),
      ));
    } else if (tool === 'line') {
      setPreview(previewLine(start, end));
    } else {
      const height = tool === 'slab' || tool === 'room' ? state.document.settings.slabThickness : 1;
      centre.y = height / 2;
      setPreview(makePreviewBox(width, height, depth, centre));
    }

    useStudioStore.getState().setStatus(`${toolLabel(tool)} · ${formatLength(Math.hypot(delta.x, delta.z), state.document.settings.units)}`);
  }

  function commitDraw(tool: ToolId, start: THREE.Vector3, rawEnd: THREE.Vector3): void {
    const state = useStudioStore.getState();
    const end = inferAxis(start, rawEnd.clone());
    const delta = end.clone().sub(start);
    const width = Math.abs(delta.x);
    const depth = Math.abs(delta.z);
    const centreX = (start.x + end.x) / 2;
    const centreZ = (start.z + end.z) / 2;
    const entities: ModelEntity[] = [];

    if (tool === 'wall') {
      const entity = wallFromPoints(start, end, state.document, `Wall ${state.document.entities.filter((item) => item.kind === 'wall').length + 1}`);
      if (entity) entities.push(entity);
    } else if (tool === 'line') {
      const length = Math.hypot(delta.x, delta.z);
      if (length >= 0.02) {
        entities.push(makeEntity('line', 'Construction line', [centreX, 0.015, centreZ], { width: length, height: 0.01, depth: 0.01 }, {
          rotation: [0, -Math.atan2(delta.z, delta.x), 0],
          color: '#1696f3',
          layerId: 'layer_site',
          metadata: { construction: true },
        }));
      }
    } else if (width >= 0.05 && depth >= 0.05) {
      if (tool === 'slab') {
        const thickness = state.document.settings.slabThickness;
        entities.push(makeEntity('slab', 'Slab', [centreX, thickness / 2, centreZ], { width, height: thickness, depth }, {
          color: '#9ca8a2',
          metadata: { material: 'Concrete' },
        }));
      } else if (tool === 'box') {
        entities.push(makeEntity('box', 'Box', [centreX, 0.5, centreZ], { width, height: 1, depth }, {
          color: state.paintColor,
          metadata: { category: 'Mass' },
        }));
      } else if (tool === 'room') {
        const thickness = state.document.settings.wallThickness;
        const height = state.document.settings.wallHeight;
        const slabThickness = state.document.settings.slabThickness;
        const minX = Math.min(start.x, end.x);
        const maxX = Math.max(start.x, end.x);
        const minZ = Math.min(start.z, end.z);
        const maxZ = Math.max(start.z, end.z);
        entities.push(makeEntity('slab', 'Room slab', [centreX, slabThickness / 2, centreZ], { width, height: slabThickness, depth }, {
          color: '#9ca8a2',
          metadata: { generatedBy: 'room-tool' },
        }));
        const walls = [
          wallFromPoints(new THREE.Vector3(minX, 0, minZ + thickness / 2), new THREE.Vector3(maxX, 0, minZ + thickness / 2), state.document, 'North wall'),
          wallFromPoints(new THREE.Vector3(maxX, 0, maxZ - thickness / 2), new THREE.Vector3(minX, 0, maxZ - thickness / 2), state.document, 'South wall'),
          wallFromPoints(new THREE.Vector3(minX + thickness / 2, 0, maxZ), new THREE.Vector3(minX + thickness / 2, 0, minZ), state.document, 'West wall'),
          wallFromPoints(new THREE.Vector3(maxX - thickness / 2, 0, minZ), new THREE.Vector3(maxX - thickness / 2, 0, maxZ), state.document, 'East wall'),
        ];
        for (const wall of walls) if (wall) {
          wall.dimensions.height = height;
          wall.position[1] = height / 2;
          entities.push(wall);
        }
      }
    }

    if (entities.length > 0) {
      state.addEntities(entities);
      state.setStatus(`Created ${entities.length === 1 ? entities[0].name : `${entities.length} building elements`}`);
    } else {
      state.setStatus('Draw a larger shape to create geometry.');
    }
  }

  function updateCursor(point: THREE.Vector3 | null): void {
    const marker = cursorMarkerRef.current;
    if (!marker) return;
    marker.visible = Boolean(point);
    if (point) marker.position.copy(point).add(new THREE.Vector3(0, 0.025, 0));
  }

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;

    const scene = new THREE.Scene();
    scene.background = new THREE.Color('#dbe8e3');
    scene.fog = new THREE.Fog('#dbe8e3', 70, 280);
    sceneRef.current = scene;

    let renderer: THREE.WebGLRenderer;
    try {
      renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false, preserveDrawingBuffer: true });
    } catch (error) {
      const fallback = window.document.createElement('div');
      fallback.className = 'viewport-unavailable';
      fallback.innerHTML = '<strong>3D graphics are unavailable</strong><span>Enable WebGL or hardware acceleration, then reload ConstructCanvas.</span>';
      mount.append(fallback);
      useStudioStore.getState().setStatus('WebGL is unavailable in this browser.');
      console.error('ConstructCanvas could not initialise WebGL.', error);
      return () => fallback.remove();
    }
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.08;
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    renderer.domElement.tabIndex = 0;
    renderer.domElement.setAttribute('aria-label', 'ConstructCanvas three-dimensional modelling viewport');
    mount.append(renderer.domElement);
    rendererRef.current = renderer;

    const perspective = new THREE.PerspectiveCamera(44, 1, 0.01, 4000);
    perspective.position.set(11, 8, 11);
    perspectiveRef.current = perspective;
    const orthographic = new THREE.OrthographicCamera(-8, 8, 6, -6, -4000, 4000);
    orthographic.position.copy(perspective.position);
    orthographicRef.current = orthographic;
    cameraRef.current = perspective;

    const controls = configureOrbit(perspective, renderer.domElement);
    controls.target.set(0, 1.2, 0);
    controls.update();
    controlsRef.current = controls;

    const hemisphere = new THREE.HemisphereLight('#f4fbf7', '#788078', 2.1);
    scene.add(hemisphere);
    const sun = new THREE.DirectionalLight('#fff7e8', 3.2);
    sun.position.set(14, 22, 10);
    sun.castShadow = true;
    sun.shadow.mapSize.set(2048, 2048);
    sun.shadow.camera.left = -30;
    sun.shadow.camera.right = 30;
    sun.shadow.camera.top = 30;
    sun.shadow.camera.bottom = -30;
    sun.shadow.camera.near = 1;
    sun.shadow.camera.far = 80;
    scene.add(sun);

    const ground = new THREE.Mesh(
      new THREE.PlaneGeometry(500, 500),
      new THREE.ShadowMaterial({ color: '#26352f', opacity: 0.14 }),
    );
    ground.rotation.x = -Math.PI / 2;
    ground.position.y = -0.004;
    ground.receiveShadow = true;
    scene.add(ground);

    const grid = new THREE.GridHelper(200, 400, '#6b7c75', '#a8b7b1');
    const materials = Array.isArray(grid.material) ? grid.material : [grid.material];
    materials.forEach((material) => {
      material.transparent = true;
      material.opacity = 0.42;
      material.depthWrite = false;
    });
    grid.position.y = 0.001;
    scene.add(grid);
    gridRef.current = grid;

    const axes = new THREE.AxesHelper(12);
    axes.position.y = 0.007;
    scene.add(axes);
    axesRef.current = axes;

    const modelRoot = new THREE.Group();
    modelRoot.name = 'ConstructCanvas model';
    scene.add(modelRoot);
    modelRootRef.current = modelRoot;

    const overlayRoot = new THREE.Group();
    overlayRoot.name = 'Tool overlays';
    scene.add(overlayRoot);
    overlayRootRef.current = overlayRoot;

    const selectionRoot = new THREE.Group();
    selectionRoot.name = 'Selection overlays';
    scene.add(selectionRoot);
    selectionRootRef.current = selectionRoot;

    const cursorMarker = new THREE.Mesh(
      new THREE.SphereGeometry(0.055, 14, 10),
      new THREE.MeshBasicMaterial({ color: '#24d57f', depthTest: false }),
    );
    cursorMarker.visible = false;
    cursorMarker.renderOrder = 10;
    overlayRoot.add(cursorMarker);
    cursorMarkerRef.current = cursorMarker;

    const transform = new TransformControls(perspective, renderer.domElement);
    transform.getHelper().visible = false;
    transform.size = 0.82;
    scene.add(transform.getHelper());
    transformRef.current = transform;
    transform.addEventListener('dragging-changed', (event) => {
      if (controlsRef.current) controlsRef.current.enabled = !(event as unknown as { value: boolean }).value;
    });
    transform.addEventListener('mouseDown', () => {
      interactionRef.current.transformBefore = useStudioStore.getState().snapshot();
    });
    transform.addEventListener('mouseUp', () => {
      const object = transform.object;
      const before = interactionRef.current.transformBefore;
      interactionRef.current.transformBefore = null;
      if (!object || !before) return;
      const id = object.userData.entityId as string | undefined;
      if (!id) return;
      const store = useStudioStore.getState();
      store.updateEntityTransient(id, {
        position: asTuple(object.position),
        rotation: [object.rotation.x, object.rotation.y, object.rotation.z],
        scale: asTuple(object.scale),
      });
      store.commitTransient(before);
      store.setStatus('Transform committed');
    });

    const resize = () => {
      const rect = mount.getBoundingClientRect();
      renderer.setSize(Math.max(1, rect.width), Math.max(1, rect.height), false);
      updateCameraProjection(rect.width, rect.height);
    };
    const observer = new ResizeObserver(resize);
    observer.observe(mount);
    resize();

    const onPointerMove = (event: PointerEvent) => {
      const store = useStudioStore.getState();
      const interaction = interactionRef.current;

      if (interaction.push) {
        const entity = store.document.entities.find((item) => item.id === interaction.push?.entityId);
        if (!entity) return;
        const delta = (interaction.push.startClientY - event.clientY) * 0.018;
        const height = Math.max(0.05, interaction.push.startHeight + delta);
        store.updateEntityTransient(entity.id, {
          dimensions: { ...entity.dimensions, height },
          position: [entity.position[0], interaction.push.baseY + height / 2, entity.position[2]],
        });
        store.setStatus(`Height ${formatLength(height, store.document.settings.units)}`);
        return;
      }

      const hit = hitEntity(event);
      if (store.hoveredId !== hit?.id) store.setHoveredId(hit?.id ?? null);
      const point = groundPoint(event);
      updateCursor(point);
      store.setCursorWorld(point ? asTuple(point) : null);

      if (DRAW_TOOLS.has(store.activeTool) && interaction.firstPoint && point) {
        updateDrawPreview(store.activeTool, interaction.firstPoint, point);
      } else if (store.activeTool === 'measure' && interaction.measurePoint) {
        const second = hit?.point ?? point;
        if (second) {
          setPreview(previewLine(interaction.measurePoint, second, '#ffbe5c'));
          store.setStatus(`Measure ${formatLength(interaction.measurePoint.distanceTo(second), store.document.settings.units)}`);
        }
      }
    };

    const onPointerDown = (event: PointerEvent) => {
      if (event.button !== 0) return;
      const store = useStudioStore.getState();
      const interaction = interactionRef.current;
      const transformControl = transformRef.current;
      if (transformControl?.dragging || (TRANSFORM_TOOLS.has(store.activeTool) && transformControl?.axis)) return;

      const hit = hitEntity(event);
      const entity = hit ? store.document.entities.find((item) => item.id === hit.id) : undefined;

      if (TRANSFORM_TOOLS.has(store.activeTool)) {
        if (hit) store.selectEntity(hit.id, event.shiftKey ? 'toggle' : 'replace');
        return;
      }

      if (store.activeTool === 'select') {
        store.selectEntity(hit?.id ?? null, event.shiftKey ? 'toggle' : 'replace');
        return;
      }

      if (store.activeTool === 'eraser') {
        if (entity?.locked) store.setStatus('Unlock the object before deleting it.');
        else if (hit) store.deleteEntities([hit.id]);
        return;
      }

      if (store.activeTool === 'paint') {
        if (entity?.locked) store.setStatus('Unlock the object before painting it.');
        else if (hit) store.updateEntity(hit.id, { color: store.paintColor });
        return;
      }

      if (store.activeTool === 'door' || store.activeTool === 'window') {
        if (!entity || entity.kind !== 'wall' || !hit) {
          store.setStatus('Click a wall to host the opening.');
          return;
        }
        if (entity.locked) {
          store.setStatus('Unlock the wall before adding an opening.');
          return;
        }
        const local = hit.point.clone().sub(new THREE.Vector3(...entity.position)).applyAxisAngle(UP, -entity.rotation[1]);
        const kind = store.activeTool;
        const width = kind === 'door' ? store.document.settings.doorWidth : store.document.settings.windowWidth;
        const height = kind === 'door' ? store.document.settings.doorHeight : store.document.settings.windowHeight;
        const sill = kind === 'door' ? 0 : store.document.settings.windowSill;
        const maxCenter = Math.max(0, entity.dimensions.width / 2 - width / 2 - 0.05);
        const center = THREE.MathUtils.clamp(local.x, -maxCenter, maxCenter);
        const opening = makeOpening(kind, center, {
          width,
          height: Math.min(height, entity.dimensions.height - sill),
          sill,
          color: kind === 'door' ? '#8d6e52' : '#76b5cf',
        });
        store.addOpening(entity.id, opening);
        store.selectEntity(entity.id);
        store.setStatus(`Added hosted ${kind}`);
        return;
      }

      if (store.activeTool === 'pushpull') {
        if (!entity || !hit || entity.kind === 'line' || entity.locked) {
          store.setStatus('Click and drag an unlocked solid element.');
          return;
        }
        interaction.push = {
          entityId: entity.id,
          startClientY: event.clientY,
          startHeight: entity.dimensions.height,
          baseY: entity.position[1] - entity.dimensions.height / 2,
          before: store.snapshot(),
        };
        renderer.domElement.setPointerCapture(event.pointerId);
        store.selectEntity(entity.id);
        return;
      }

      if (store.activeTool === 'measure') {
        const point = hit?.point ?? groundPoint(event);
        if (!point) return;
        if (!interaction.measurePoint) {
          interaction.measurePoint = point.clone();
          store.setMeasurementResult(null);
          store.setStatus('Choose the second measurement point.');
        } else {
          const distance = interaction.measurePoint.distanceTo(point);
          store.setMeasurementResult(distance);
          store.setStatus(`Measured ${formatLength(distance, store.document.settings.units)}`);
          interaction.measurePoint = null;
          setPreview(null);
        }
        return;
      }

      if (DRAW_TOOLS.has(store.activeTool)) {
        const point = groundPoint(event);
        if (!point) return;
        if (!interaction.firstPoint) {
          interaction.firstPoint = point.clone();
          store.setStatus('Choose the second point. Press Esc to cancel.');
        } else {
          commitDraw(store.activeTool, interaction.firstPoint, point);
          interaction.firstPoint = null;
          setPreview(null);
        }
      }
    };

    const onPointerUp = (event: PointerEvent) => {
      const interaction = interactionRef.current;
      if (!interaction.push) return;
      const store = useStudioStore.getState();
      store.commitTransient(interaction.push.before);
      store.setStatus('Push/Pull committed');
      interaction.push = null;
      if (renderer.domElement.hasPointerCapture(event.pointerId)) renderer.domElement.releasePointerCapture(event.pointerId);
    };

    const onDoubleClick = () => frameBounds(modelBounds(true));
    const onContextMenu = (event: MouseEvent) => event.preventDefault();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      interactionRef.current.firstPoint = null;
      interactionRef.current.measurePoint = null;
      setPreview(null);
      useStudioStore.getState().setStatus('Tool action cancelled');
    };

    renderer.domElement.addEventListener('pointermove', onPointerMove);
    renderer.domElement.addEventListener('pointerdown', onPointerDown);
    renderer.domElement.addEventListener('pointerup', onPointerUp);
    renderer.domElement.addEventListener('dblclick', onDoubleClick);
    renderer.domElement.addEventListener('contextmenu', onContextMenu);
    window.addEventListener('keydown', onKeyDown);

    let frame = 0;
    const animate = () => {
      frame = requestAnimationFrame(animate);
      controlsRef.current?.update();
      selectionRootRef.current?.children.forEach((child) => {
        if (child instanceof THREE.BoxHelper) child.update();
      });
      renderer.render(scene, currentCamera());
    };
    animate();

    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
      renderer.domElement.removeEventListener('pointermove', onPointerMove);
      renderer.domElement.removeEventListener('pointerdown', onPointerDown);
      renderer.domElement.removeEventListener('pointerup', onPointerUp);
      renderer.domElement.removeEventListener('dblclick', onDoubleClick);
      renderer.domElement.removeEventListener('contextmenu', onContextMenu);
      window.removeEventListener('keydown', onKeyDown);
      transform.dispose();
      controls.dispose();
      disposeObject(scene);
      renderer.dispose();
      renderer.domElement.remove();
    };
  }, []);

  useEffect(() => {
    const scene = sceneRef.current;
    const oldRoot = modelRootRef.current;
    if (!scene || !oldRoot) return;

    const newRoot = createDocumentRoot(document, { includeEdges: true });
    newRoot.name = 'ConstructCanvas model';
    scene.remove(oldRoot);
    disposeObject(oldRoot);
    scene.add(newRoot);
    modelRootRef.current = newRoot;
    objectMapRef.current.clear();
    newRoot.children.forEach((object) => {
      const id = object.userData.entityId as string | undefined;
      if (id) objectMapRef.current.set(id, object);
    });

    const background = new THREE.Color(document.settings.background);
    scene.background = background;
    if (scene.fog instanceof THREE.Fog) scene.fog.color.copy(background);
    if (rendererRef.current) rendererRef.current.shadowMap.enabled = document.settings.shadows;
    if (gridRef.current) gridRef.current.visible = document.settings.showGrid;
    if (axesRef.current) axesRef.current.visible = document.settings.showAxes;
    syncTransformControl();
    updateSelectionHelpers();
  }, [document]);

  useEffect(() => {
    syncTransformControl();
    updateSelectionHelpers();
  }, [selectedIds, hoveredId, activeTool]);

  useEffect(() => {
    const interaction = interactionRef.current;
    interaction.firstPoint = null;
    interaction.measurePoint = null;
    setPreview(null);
    const canvas = rendererRef.current?.domElement;
    if (canvas) {
      canvas.style.cursor = activeTool === 'select' ? 'default'
        : TRANSFORM_TOOLS.has(activeTool) ? 'default'
          : activeTool === 'eraser' ? 'not-allowed'
            : 'crosshair';
    }
  }, [activeTool]);

  useImperativeHandle(ref, () => ({
    fitView: (selectionOnly = false) => frameBounds(modelBounds(selectionOnly)),
    setStandardView: (view) => {
      const box = modelBounds(false);
      const directions: Record<StandardView, THREE.Vector3> = {
        iso: new THREE.Vector3(1, 0.8, 1),
        top: new THREE.Vector3(0.0001, 1, 0.0001),
        front: new THREE.Vector3(0, 0.12, 1),
        back: new THREE.Vector3(0, 0.12, -1),
        left: new THREE.Vector3(-1, 0.12, 0),
        right: new THREE.Vector3(1, 0.12, 0),
      };
      frameBounds(box, directions[view]);
    },
    toggleProjection: () => switchProjection(projectionRef.current === 'perspective' ? 'orthographic' : 'perspective'),
    getProjection: () => projectionRef.current,
    getSceneSnapshot: (name) => ({
      name,
      cameraPosition: asTuple(currentCamera().position),
      cameraTarget: asTuple(currentControls().target),
      projection: projectionRef.current,
    }),
    restoreScene: (savedScene) => {
      if (!cameraRef.current || !controlsRef.current) return;
      switchProjection(savedScene.projection);
      currentCamera().position.set(...savedScene.cameraPosition);
      currentControls().target.set(...savedScene.cameraTarget);
      currentCamera().lookAt(currentControls().target);
      currentControls().update();
    },
    exportModel: (format) => exportDocument(useStudioStore.getState().document, format),
    capturePng: async () => {
      const renderer = rendererRef.current;
      const scene = sceneRef.current;
      if (!renderer || !scene) return;
      renderer.render(scene, currentCamera());
      const blob = await new Promise<Blob | null>((resolve) => renderer.domElement.toBlob(resolve, 'image/png'));
      if (blob) downloadBlob(blob, `${useStudioStore.getState().document.name.replace(/[^a-z0-9-_]+/gi, '-') || 'model'}.png`);
    },
  }));

  return (
    <div className="viewport-shell" ref={mountRef}>
      <div className="viewport-help">
        <span><kbd>Middle drag</kbd> orbit</span>
        <span><kbd>Right drag</kbd> pan</span>
        <span><kbd>Wheel</kbd> zoom</span>
      </div>
    </div>
  );
});

function toolLabel(tool: ToolId): string {
  return tool.charAt(0).toUpperCase() + tool.slice(1);
}
